// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IRawStyle, IStyle } from "@fluentui/react";
import type { AppContextType } from "@1js/acui-context";
import { getScalingClassNameMLR, getScalingClassNameSLR } from ".";
import type {
  RibbonScalingStep,
  RibbonScalingTransformation,
} from "../../../Scaling";
import type { RibbonMode } from "../../Ribbon";
import type {
  EstimatedScalingWidthDict,
  RibbonScalingStateMLRWithCacheKeys,
  RibbonScalingStateSLRWithCacheKeys,
  ScalingClassNameProperties,
  ScalingStepsStyle,
  Selectors,
} from "../ribbonBottomBarScalingTypes";
import { getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector } from "./SharedScaling";

const classNameCache: { [key: string]: CacheEntry } = {};
const dropPreviewGalleryItemCountCache: Record<
  RibbonMode,
  { [id: string]: number }
> = {
  SingleLine: {},
  Multiline: {},
};
const galleriesConvertedToFlyoutsCache: Record<
  RibbonMode,
  { [galleryId: string]: boolean }
> = {
  SingleLine: {},
  Multiline: {},
};

/**
 * Gets the selector for making the ghost flyout (that appears at the galleryToFlyout scaling step) visible
 * @param scalingStep The scaling step to check
 * @returns A selector for making the ghost flyout visible, if applicable, empty string otherwise
 */
export const getGhostFlyoutVisibleSelector = (
  scalingStep: RibbonScalingTransformation
) => {
  // Show the ghostFlyout if we are transitioning from gallery to flyout
  if (scalingStep.type === "GalleryToFlyout") {
    return getCoreRibbonElementForGalleriesConvertedToFlyoutAnchorsSelector(
      scalingStep.controlId
    );
  }

  return "";
};

export type CacheEntry = {
  className: string;
  estimatedWidths: EstimatedScalingWidthDict;
};

/**
 * Returns a simple cache object is able to store classNames with a given key.
 */
export const getClassNameCache = <
  T extends
    | RibbonScalingStateSLRWithCacheKeys
    | RibbonScalingStateMLRWithCacheKeys,
>() => {
  return {
    /**
     * Checks if the provided data has a cacheKey. If it has a cacheKey and there is a
     * corresponding entry in the classNameCache, then it will return that value.
     * Returns undefined otherwise.
     */
    getCachedEntry: (data: T): CacheEntry | undefined => {
      return (
        (data && data.cacheKey && classNameCache[data.cacheKey]) || undefined
      );
    },
    /**
     * Should be called whenever there is a new measurement associated with a given data object.
     * If the data has a cacheKey, store that measurement in the measurementsCache.
     */
    addMeasurementToCache: (
      data: T,
      className: string,
      estimatedWidths: EstimatedScalingWidthDict
    ): void => {
      if (data.cacheKey) {
        classNameCache[data.cacheKey] = {
          className,
          estimatedWidths,
        };
      }
    },
  };
};

/**
 * Adds the styles for applying the initial scaling steps
 * Note, these scaling steps are always applied
 * @param scalingState The scaling state to build the styles for
 * @param style The style object to write the new scaling styles
 * @param getScalingStepsStylesBase Function for building selectors
 */
export const addInitialScalingStepsStyles = <
  T extends
    | RibbonScalingStateSLRWithCacheKeys
    | RibbonScalingStateMLRWithCacheKeys,
>(
  scalingState: T,
  style: ScalingStepsStyle,
  getScalingStepsStylesBase: (
    scalingStepIndex: number,
    scalingState: T,
    appContext: AppContextType
  ) => Selectors,
  appContext: AppContextType
) => {
  // Loop over the InitialScalingSteps (zero to minimumScalingStepsApplied)
  for (
    let scalingStepIndex = 0;
    scalingStepIndex < scalingState.minimumScalingStepsToApply;
    scalingStepIndex = scalingStepIndex + 1
  ) {
    // Add the new selectors, these do not need a max-width because
    // they are initial scaling steps and hence are always applied
    style.selectors = {
      ...style.selectors,
      ...getScalingStepsStylesBase(scalingStepIndex, scalingState, appContext),
    };
  }
};

/**
 * Adds max-width media query selectors to the given style object
 * @param style Style object to add selectors to
 * @param maxWidth The max width to use for the media queries
 * @param scalingState The ribbon scaling state
 * @param scalingStepIndex The index of the current scaling step
 * @param getScalingStepsStylesBase Function for building selectors
 * @param layoutChangeCount Optional the number of layout counts up to the current scaling step index (used in MLR)
 */
export const addMaxWidthSelectors = <
  T extends
    | RibbonScalingStateSLRWithCacheKeys
    | RibbonScalingStateMLRWithCacheKeys,
>(
  style: ScalingStepsStyle,
  maxWidth: number,
  scalingState: T,
  scalingStepIndex: number,
  getScalingStepsStylesBase: (
    scalingStepIndex: number,
    scalingState: T,
    appContext: AppContextType,
    layoutChangeCount?: number
  ) => Selectors,
  appContext: AppContextType,
  layoutChangeCount?: number
) => {
  style.selectors[`@media (max-width: ${maxWidth}px)`] = {
    selectors: {
      ...((style.selectors[`@media (max-width: ${maxWidth}px)`] as IRawStyle)
        ?.selectors || {}),
      ...getScalingStepsStylesBase(
        scalingStepIndex,
        scalingState,
        appContext,
        layoutChangeCount
      ),
    },
  };
};

/**
 * Get a selector comprised of all scaling steps in the array
 * @param scalingSteps The scaling step to build the selector from
 * @returns A selector for the given scaling step array
 */
export const getSelectorsForScalingSteps = (
  scalingSteps: Readonly<RibbonScalingStep>[],
  getCoreScalingStepSelector: (
    scalingStep: Readonly<RibbonScalingStep>,
    appContext: AppContextType,
    topLevelScalingIndex?: number
  ) => string,
  appContext: AppContextType,
  topLevelScalingIndex?: number,
  moveToOverflowFullClassNameSelector?: string
) => {
  // Combine the selectors for each step in the array
  let selectors: string = scalingSteps.reduce(
    (resultingSelectors, currentStep) =>
      `${resultingSelectors}, ${getCoreScalingStepSelector(
        currentStep,
        appContext,
        topLevelScalingIndex
      )}`,
    ""
  );

  // refine the scaling step selectors and the leading comma
  selectors = refineScalingStepSelectors(
    selectors,
    moveToOverflowFullClassNameSelector
  );

  return selectors;
};

/**
 * Helper function to remove duplicate MoveToOverflow selectors as well as the leading ', '
 * @param selectors The selectors string to refine
 * @param moveToOverflowFullClassNameSelector Optional MoveToOverflow selector that need to be duplicated (used in SLR)
 * @returns A refined version of the given selector
 */
export function refineScalingStepSelectors(
  selectors: string,
  moveToOverflowFullClassNameSelector?: string
) {
  let refinedSelectors = selectors;

  // If we have moveToOverflow, make sure there's only one since they are all the same
  if (moveToOverflowFullClassNameSelector) {
    const moveToOverflowRegex = new RegExp(
      `, \\${moveToOverflowFullClassNameSelector}`,
      "gi"
    );
    if (moveToOverflowRegex.test(refinedSelectors)) {
      refinedSelectors = `${refinedSelectors.replace(
        moveToOverflowRegex,
        ""
      )}, ${moveToOverflowFullClassNameSelector}`;
    }
  }

  // Remove the extra leading ', ' since that will make this selector
  // match everything when given to mergeStyles!
  const leadingCommaRegex = new RegExp(`^, `, "gi");
  if (leadingCommaRegex.test(refinedSelectors)) {
    refinedSelectors = refinedSelectors.replace(leadingCommaRegex, "");
  }

  return refinedSelectors;
}

/**
 * Gets a selector that references the preview gallery item that should be dropped.
 * Gallery preview items are always dropped from the end.
 * @param id The id of the preview gallery item that is being dropped
 * @returns A selector that references the preview gallery item that should be dropped
 */
export function getDropPreviewItemFromGalleryIndexSelector(
  id: string,
  ribbonMode: RibbonMode
) {
  if (dropPreviewGalleryItemCountCache[ribbonMode][id] === undefined) {
    dropPreviewGalleryItemCountCache[ribbonMode][id] = 1;
  }

  dropPreviewGalleryItemCountCache[ribbonMode][id] =
    dropPreviewGalleryItemCountCache[ribbonMode][id] + 1;

  // Build a selector to give the correct preview item
  // Note, we start from index two because we have to account for the preview expand button
  return `button:nth-last-child(${dropPreviewGalleryItemCountCache[ribbonMode][id]})`;
}

export function getGalleriesConvertedToFlyoutByIdMode(
  id: string,
  ribbonMode: RibbonMode
) {
  return galleriesConvertedToFlyoutsCache[ribbonMode][id];
}

export function setGalleriesConvertedToFlyoutByIdMode(
  id: string,
  ribbonMode: RibbonMode,
  isConvertedToFlyout: boolean
) {
  galleriesConvertedToFlyoutsCache[ribbonMode][id] = isConvertedToFlyout;
}

export function clearScaledGalleryCaches(ribbonMode: RibbonMode) {
  dropPreviewGalleryItemCountCache[ribbonMode] = {};
  galleriesConvertedToFlyoutsCache[ribbonMode] = {};
}

/**
 * Gets a className that corresponds to the given scaling state
 * @param scalingProperties Properties used to compute the className
 * @returns A className for the given scaling state
 */
export function getScalingClassName<
  P extends
    | RibbonScalingStateMLRWithCacheKeys
    | RibbonScalingStateSLRWithCacheKeys,
>(
  scalingProperties: ScalingClassNameProperties<P>,
  mergeStyles: (...args: IStyle[]) => string // Passing this in as a param so this module doesn't need to take a hard dependency on fluent
) {
  // Only allow for initialRender if we are also ensuringFullWidths
  scalingProperties.initialRender =
    !!scalingProperties.initialRender && !!scalingProperties.ensureFullWidths;
  return scalingProperties.scalingState.type === "SingleLine"
    ? getScalingClassNameSLR(
        scalingProperties as ScalingClassNameProperties<RibbonScalingStateSLRWithCacheKeys>,
        mergeStyles
      )
    : getScalingClassNameMLR(
        scalingProperties as ScalingClassNameProperties<RibbonScalingStateMLRWithCacheKeys>,
        mergeStyles
      );
}
